import React, { forwardRef } from "react";
import styled, { css } from "styled-components";
import { Input, InputRef } from "antd";
import { ColorPalette } from "./color/colorPalette";

const inputStyles = css<{ size: "large" | "xlarge" }>`
  border-radius: 4px;
  border-width: 0.7px;
  padding: 8px 12px;
  gap: 8px;
  height: ${({ size }) => (size === "large" ? "40px" : "48px")};

  /* حالت بدون آیکون */
  &.ant-input {
    font-family: "Vazir", sans-serif !important;
    font-size: 14px;
    font-weight: 325;
    line-height: 25.2px;
    text-align: right;
    border-color: ${ColorPalette.gray[500]};

    &:hover {
      border-color: ${ColorPalette.gray[900]};
    }
    &:focus {
      border-color: ${ColorPalette.primaryBlue[500]};
    }
    &.filled {
      background-color: ${ColorPalette.gray[50]};
      border-color: ${ColorPalette.gray[600]};
      color: ${ColorPalette.gray[950]};
    }
    &.disabled {
      border-color: ${ColorPalette.gray[200]};
      background-color: ${ColorPalette.gray[50]};
    }
    &.invalid {
      border-color: ${ColorPalette.red[300]};
      background-color: #ffffff;
    }
  }

  /* حالت با آیکون (prefix/suffix) */
  &.ant-input-affix-wrapper {
    font-family: "Vazir", sans-serif !important;
    font-size: 14px;
    border-color: ${ColorPalette.gray[500]};

    &:hover {
      border-color: ${ColorPalette.gray[900]};
    }
    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${ColorPalette.primaryBlue[500]};
      box-shadow: none;
    }
    &.filled {
      background-color: ${ColorPalette.gray[50]};
      border-color: ${ColorPalette.gray[600]};
      color: ${ColorPalette.gray[950]};
    }
    &.disabled,
    &.ant-input-affix-wrapper-disabled {
      border-color: ${ColorPalette.gray[200]};
      background-color: ${ColorPalette.gray[50]};
    }
    &.invalid {
      border-color: ${ColorPalette.red[300]};
      background-color: #ffffff;
    }

    .ant-input {
      font-family: "Vazir", sans-serif !important;
      font-size: 14px;
      font-weight: 325;
      line-height: 25.2px;
      text-align: right;
      background-color: transparent;
    }
  }
`;

const StyledInput = styled(Input)<{ size: "large" | "xlarge" }>`
  ${inputStyles}
`;

interface InputWithIconsProps {
  size: "large" | "xlarge";
  className?: string;
  LeftIconEnabled?: boolean;
  RightIconEnabled?: boolean;
  LeftIcon?: any;
  RightIcon?: any;
  disabled?: boolean;
  invalid?: boolean;
  readOnlyMode?: boolean;
  type?: string;
  value?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClickLeftIcon?: () => void;
  onClickRightIcon?: () => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
  autoFocus?: boolean;
}

const InputWithIcons = forwardRef<InputRef, InputWithIconsProps>(
  (
    {
      size,
      className,
      LeftIconEnabled,
      RightIconEnabled,
      LeftIcon,
      RightIcon,
      disabled = false,
      invalid = false,
      readOnlyMode = false,
      type = "text",
      value,
      placeholder,
      onChange,
      onBlur,
      onClickLeftIcon,
      onClickRightIcon,
      onClick,
      onKeyPress,
      onKeyDown,
      style,
      autoFocus = false,
    },
    ref
  ) => {
    const inputClass = [
      value ? "filled" : "",
      disabled ? "disabled" : "",
      invalid ? "invalid" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const handleIconClick = (
      event: React.MouseEvent,
      callback?: () => void
    ) => {
      event.stopPropagation();
      if (callback) callback();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (readOnlyMode) {
        e.preventDefault();
        return;
      }
      if (onKeyDown) onKeyDown(e);
    };

    return (
      <StyledInput
        ref={ref}
        type={type}
        size={size}
        className={inputClass}
        disabled={disabled}
        readOnly={readOnlyMode}
        onClick={onClick}
        suffix={
          LeftIconEnabled ? (
            <LeftIcon
              className="icon left-icon"
              onClick={(e: any) => handleIconClick(e, onClickLeftIcon)}
            />
          ) : null
        }
        prefix={
          RightIconEnabled ? (
            <RightIcon
              className="icon right-icon"
              onClick={(e: any) => handleIconClick(e, onClickRightIcon)}
            />
          ) : null
        }
        value={value}
        placeholder={placeholder}
        onChange={!readOnlyMode ? onChange : undefined}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        onKeyDown={handleKeyDown}
        style={style}
        autoFocus={autoFocus}
      />
    );
  }
);

export default InputWithIcons;
